import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import TableWrapper from "../../core/BaseComponents/TableWrapper/TableWrapper";
import dayjs from "dayjs";
import { Tooltip, Autocomplete, TextField, CircularProgress } from "@mui/material";
import { MdModeEditOutline } from "react-icons/md";
import { callAPI } from "../../../axios";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteCustomWelcomeMessage from "./DeleteCustomWelcomeMessage";
import WelcomeMessageSuccessModal from "../WelcomeMessageConfig/WelcomeMessageSuccessModal";
import WelcomeMessageCheckInOutModal from "../WelcomeMessageConfig/WelcomeMessageCheckInOutModal";
import WelcomeMessageCharLimitModal from "../WelcomeMessageConfig/WelcomeMessageCharLimitModal";
import {
  GUEST_NAME_PLACEHOLDER,
  resolveWelcomeMessageForRoom,
} from "../WelcomeMessageConfig/welcomeMessageTemplateUtils";
// import ConfigrationActions from "./ConfigrationActions";
import Custom_Paingation from "../../core/BaseComponents/Custom_Paingation";
import { ScrollToTop, getChainIdLS, isValidRole } from "../../../utils/utils";
import { useSelector } from "react-redux";

const MAX_CHARS = 60;
const MAX_GUEST_NAME_CHARS = 15;
const MIN_GUEST_NAME_CHARS = 3;

// Live substitution used while the user is typing.
const resolveGuestNameLive = (template, guestName) =>
  template ? template.split(GUEST_NAME_PLACEHOLDER).join(guestName || "") : "";

const getPlaceholderCount = (template) =>
  template?.includes(GUEST_NAME_PLACEHOLDER)
    ? template.split(GUEST_NAME_PLACEHOLDER).length - 1
    : 0;

// Leave room for at least MIN_GUEST_NAME_CHARS (or current guest length if longer)
const getMaxTemplateOnlyLen = (guestLen, placeholderCount) =>
  placeholderCount > 0
    ? MAX_CHARS - placeholderCount * Math.max(MIN_GUEST_NAME_CHARS, guestLen)
    : MAX_CHARS;

const getMaxGuestNameCharsForTemplate = (template) => {
  const placeholderCount = getPlaceholderCount(template);
  if (!placeholderCount) {
    return MAX_GUEST_NAME_CHARS;
  }
  const templateOnlyPreviewLen = resolveGuestNameLive(template, "").length;
  const guestCharBudget = MAX_CHARS - templateOnlyPreviewLen;
  if (guestCharBudget <= 0) return MIN_GUEST_NAME_CHARS;
  const maxPerGuest = Math.floor(guestCharBudget / placeholderCount);
  return Math.min(
    MAX_GUEST_NAME_CHARS,
    Math.max(MIN_GUEST_NAME_CHARS, maxPerGuest)
  );
};

const wouldExceedWelcomeMessageLimit = (oldValue, newValue, guestName) => {
  const guestStr = guestName || "";
  const currentPreviewLen = resolveGuestNameLive(oldValue, guestStr).length;
  const newPreviewLen = resolveGuestNameLive(newValue, guestStr).length;

  if (newPreviewLen > MAX_CHARS && newPreviewLen > currentPreviewLen) {
    return true;
  }

  const placeholderCount = getPlaceholderCount(newValue);
  const maxTemplateOnlyLen = getMaxTemplateOnlyLen(
    guestStr.length,
    placeholderCount
  );
  const currentTemplateOnlyLen = resolveGuestNameLive(oldValue, "").length;
  const newTemplateOnlyLen = resolveGuestNameLive(newValue, "").length;

  return (
    newTemplateOnlyLen > maxTemplateOnlyLen &&
    newTemplateOnlyLen > currentTemplateOnlyLen
  );
};

const getRejectedWelcomeMessageCursorPosition = (oldValue, newValue, selectionStart) => {
  const charsDelta = newValue.length - oldValue.length;
  const cursorAfterChange = selectionStart ?? newValue.length;

  return Math.max(0, cursorAfterChange - charsDelta);
};

const fitWelcomeMessageInsert = (oldValue, insertStart, insertEnd, insertText, guestName) => {
  let inserted = insertText;
  let candidate = `${oldValue.slice(0, insertStart)}${inserted}${oldValue.slice(insertEnd)}`;

  while (
    inserted.length > 0 &&
    wouldExceedWelcomeMessageLimit(oldValue, candidate, guestName)
  ) {
    inserted = inserted.slice(0, -1);
    candidate = `${oldValue.slice(0, insertStart)}${inserted}${oldValue.slice(insertEnd)}`;
  }

  if (wouldExceedWelcomeMessageLimit(oldValue, candidate, guestName)) {
    return null;
  }

  return {
    value: candidate,
    cursorPos: insertStart + inserted.length,
  };
};

const rejectWelcomeMessageChange = (target, oldValue, newValue, pendingSelectionRef) => {
  const restorePos = getRejectedWelcomeMessageCursorPosition(
    oldValue,
    newValue,
    target.selectionStart
  );

  pendingSelectionRef.current = {
    start: restorePos,
    end: restorePos,
  };
  target.value = oldValue;
  target.setSelectionRange(restorePos, restorePos);
};

function CustomWelcomeMessageList({ templates = [] }) {
  const today = new Date().toISOString().split("T")[0];
  const [publishUnPublishList, setPublishUnPublishList] = useState({});
  const [roomDetails, setRoomDetails] = useState([]);
  const [cdsnList, setCdsnList] = useState('');
  const [confirmDeletePublishUnPublish, setConfirmDeletePublishUnPublish] = useState("");
  const [confirmValidate, setConfirmValidate] = useState(false);
  const [deletePublishUnPublishPopUp, setDeletePublishUnPublishPopUp] = useState(false);
  const [publishUnPublishPopUp, setPublishUnPublishPopUp] = useState(false);
  const [editPublishUnPublish, setEditPublishUnPublish] = useState(false);
  const [editPublishUnPublishUpdate, setEditPublishUnPublishUpdate] = useState(false);
  const [createPublishUnPublishSuccessMsg, setCreatePublishUnPublishSuccessMsg] = useState('');
  const [publishUnPublishSid, setPublishUnPublishSid] = useState("");
  // const [hotelName, setHotelName] = useState('');
  const [flag, setFlag] = useState(false);
  const [apiWelcomeMessage, setApiWelcomeMessage] = useState("")
  const [previewMessage, setPreviewMessage] = useState("");
  const [previewCharCount, setPreviewCharCount] = useState(0);
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const wasOverLimitRef = useRef(false);
  const welcomeMessageTextareaRef = useRef(null);
  const welcomeMessageSelectionRef = useRef({ start: 0, end: 0 });
  const pendingWelcomeMessageSelectionRef = useRef(null);
  const [loader, setLoader] = useState(false);
  const recordsPerPage = 10;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
    watch,
    setValue,
    clearErrors
  } = useForm({
    mode: 'onChange'
  });
  const { chainId } = useSelector((state) => state.chainIdReducer);

  let validPermissionClassName = isValidRole("WELCOME_MESSAGES_MODIFY")
    ? ""
    : "btn-disable-permission";

  const [selectedPage, setSelectedPage] = useState(1);
  const [totalPageCount, setTotalPageCount] = useState(1);
  const [paginationError, setPaginationError] = useState("")
  const [inputPageValue, setInputPageValue] = useState(1)

  const saveWelcomeMessage = (data, isPublished) => {
    const resolvedPreviewLen = resolveGuestNameLive(
      data.welcomeMessage || "",
      data.guestName || ""
    ).length;
    if (resolvedPreviewLen > MAX_CHARS) return;

    let method = "POST";
    let url = `/create-welcome-message`;

    let payload = {
      ...data,
      isPublished: isPublished
    };

    if (editPublishUnPublishUpdate) {
      method = "PUT";
      url = `/update-welcome-message`;
      payload.id = publishUnPublishSid?.id;
      payload.isPublished = false;
    }

    setLoader(true);
    callAPI(url, method, payload, { chainId: getChainIdLS() })
      .then(function (response) {
        reset({
          roomNo: '',
          cdsn: '',
          guestName: 'Guest',
          welcomeMessage: ''
        });
        setApiWelcomeMessage("");
        setPreviewMessage("");
        getAvailableRooms();
        setCreatePublishUnPublishSuccessMsg(response?.message);
        publishUnPublishListApi();
        setLoader(false);
      })
      .catch(function (error) {
        console.warn(error.response?.data?.errorMessage);
        setLoader(false);
      });
  };

  const onSubmit = (data) => saveWelcomeMessage(data, true);

  const onSave = (data) => saveWelcomeMessage(data, false);

  const isPublishUnPublishDeletedRef = useRef({
    isPublishUnPublishDeletedModalData: null,
    userConformationModalData: null,
  });

  const isPublishedUnPublishedRef = useRef({
    isPublishUnPublishModalData: null,
  });


  const publishUnPublishPopUpHandler = (row) => {
    setPublishUnPublishPopUp(true);
    const newModalRefData = {
      isPublishUnPublishModalData: {
        message: `Do you really want to ${row?.isPublished.toLowerCase() !== 'published' ? 'Check-In' : 'Check-Out'}?`,
        row
      }
    };
    isPublishedUnPublishedRef.current = newModalRefData;
  }

  const deletePublishUnPublishPopUpHandler = (row) => {
    setDeletePublishUnPublishPopUp(true);
    const newModalRefData = {
      isPublishUnPublishDeletedModalData: {
        row
      },
      userConformationModalData: null,
    };
    isPublishUnPublishDeletedRef.current = newModalRefData;
  }

  const deletePublishUnPublishHandler = () => {
    setConfirmValidate(true);
    if (confirmDeletePublishUnPublish === "") {
      return
    }

    if (confirmDeletePublishUnPublish === `Delete Room No ${isPublishUnPublishDeletedRef.current?.isPublishUnPublishDeletedModalData.row.roomNo} Welcome Message`) {
      setConfirmValidate(false)
      const { row } = isPublishUnPublishDeletedRef.current?.isPublishUnPublishDeletedModalData;
      setLoader(true);
      callAPI(`delete-welcome-message?Id=${row?.id}`, "DELETE")
        .then((response) => {
          let successMessage = response.message
          const newModalRefData = {
            isPublishUnPublishDeletedModalData: null,
            userConformationModalData: {
              successMessage
            },
          };
          isPublishUnPublishDeletedRef.current = newModalRefData;
          publishUnPublishListApi();
          getAvailableRooms();
          setLoader(false);
        })
        .catch((error) => {
          console.warn(error);
          setLoader(false);
        });
    }
    reset({
      roomNo: '',
      cdsn: '',
      guestName: 'Guest',
      welcomeMessage: ''
    });
    setApiWelcomeMessage("");
    setPreviewMessage("");
  }

  const renderActionIcon = (row) => {
    return (
      isValidRole("HOTEL_CHANNELS_MODIFY") ? <>
        {
          row?.isPublished.toLowerCase() !== 'published' ? <>
            <Tooltip title="Edit" arrow>
              <span>
                <MdModeEditOutline
                  className="edit-icon-enable"
                  onClick={() => { setPublishUnPublishSid(row); getPublishUnPublishListSidHandler(row) }}
                />
              </span>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <DeleteIcon
                className="delete-icon-enable"
                onClick={() => {
                  deletePublishUnPublishPopUpHandler(row);
                }}
              />
            </Tooltip>
          </> : <>
            <Tooltip title="Check-Out to perform actions">
              <span>
                <MdModeEditOutline className="edit-icon-disable" arrow />
              </span>
            </Tooltip>
            <Tooltip title="Check-Out to perform actions">
              <DeleteIcon className="delete-icon-disable" arrow />
            </Tooltip>
          </>
        }
        <Tooltip title={`${row?.isPublished.toLowerCase() !== 'published' ? 'Check-In' : 'Check-Out'}`} className="cursor-pointer" arrow>
          <button
            type="button"
            className={`ms-3 ${row?.isPublished.toLowerCase() !== 'published' ? 'published' : 'un-published'}`}
            onClick={() => publishUnPublishPopUpHandler(row)}
          >
            {row?.isPublished.toLowerCase() !== 'published' ? 'Check-In' : 'Check-Out'}
          </button>
        </Tooltip>
      </> :
        <>
          <Tooltip title="Don't have permission to edit">
            <span>
              <MdModeEditOutline className="edit-icon-disable" arrow />
            </span>
          </Tooltip>
          <Tooltip title="Don't have permission to delete">
            <DeleteIcon className="delete-icon-disable" arrow />
          </Tooltip>
          <Tooltip title="Don't have permission to delete" className="cursor-pointer" arrow>
            <span>
              <button
                type="button"
                className={`ms-3 ${row?.isPublished.toLowerCase() !== 'published' ? 'published' : 'un-published'} ${validPermissionClassName}`}
              // onClick={() => publishUnPublishPopUpHandler(row)}
              >
                {row?.isPublished.toLowerCase() !== 'published' ? 'Check-In' : 'Check-Out'}
              </button>
            </span>
          </Tooltip>
        </>
    );
  };

  const columns = [
    { field: "createdOn", headerName: "Created On", flex: 1, row: true, renderCell: (row) => <p className="mb-0">{dayjs(row.createdOn).format("DD MMM YYYY | HH:mm")}</p> },
    { field: "roomNo", headerName: "Room No", flex: 1 },
    { field: "cdsn", headerName: "CDSN", flex: 1, row: true, renderCell: (row) => <p className="mb-0">{row?.cdsn.map((item, index) => <span>{index ? ', ' : ''}{item}</span>)}</p> },
    // { field: "checkinDateTime", headerName: "Check-in date", flex: 1, row: true, renderCell: (row) => <p className="mb-0">{dayjs(row.checkinDateTime).format("DD-MM-YYYY")}</p> },
    // { field: "checkoutDateTime", headerName: "Check-out date", flex: 1, row: true, renderCell: (row) => <p className="mb-0">{dayjs(row.checkoutDateTime).format("DD-MM-YYYY")}</p> },
    { field: "guestName", headerName: "Guest Name", flex: 1 },
    { field: "welcomeMessage", headerName: "Welcome Message", flex: 1, row: true, renderCell: (row) => <Tooltip title={row?.welcomeMessage}><p className={`mb-0 ${row?.welcomeMessage.length > 10 ? 'overflow-ellipsis' : ''}`}>{row?.welcomeMessage}</p></Tooltip> },
    {
      field: "actionCell",
      row: true,
      headerName: "Actions",
      flex: 0.5,
      renderCell: renderActionIcon,
      sortDirection: false,
      disableSortIcon: true,
    },
  ];

  const publishUnPublishListApi = () => {
    setLoader(true);
    const apiPath = `/welcome-message?pageNo=${selectedPage}&recordsPerPage=${recordsPerPage}`;
    callAPI(apiPath, "POST", {}, { chainId: getChainIdLS() })
      .then((response) => {
        setPublishUnPublishList(response)
        setTotalPageCount(response?.totalPages ? response?.totalPages : 1)
        setPaginationError("");
        setLoader(false);
      })
      .catch((err) => {
        console.warn(err.message);
        setLoader(false);
      });
  };

  useEffect(() => {
    publishUnPublishListApi();
  }, [selectedPage, chainId]);

  const getAvailableRooms = () => {
    callAPI("/available_rooms", "GET", {}, { chainId: getChainIdLS() })
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setRoomDetails(
          list.map((item) =>
            typeof item === "string"
              ? { roomNo: item, roomType: null }
              : { roomNo: item.roomNo, roomType: item.roomType ?? null }
          )
        );
      })
      .catch((err) => {
        console.warn(err.message);
      });
  };
  useEffect(() => {
    getAvailableRooms();
    cancelPublishUnPublishHandler();
    // const hotelName = JSON.parse(localStorage.getItem("userDetails"))?.chainIdHotelNameMapping?.filter((hotel) => hotel?.chainId === chainId?.chainId)
    // setHotelName(hotelName);
  }, [chainId]);

  useEffect(() => {
    ScrollToTop();
    if (watch("roomNo")) {
      callAPI(`getcdsn/room-no?roomNo=${watch("roomNo")}`, "GET", {}, { chainId: getChainIdLS() })
        .then((res) => {
          setCdsnList(res?.cdsn);
          setValue('cdsn', res?.cdsn);
        })
        .catch((err) => {
          console.warn(err.message);
        });
    } else {
      setCdsnList('');
    }
  }, [watch("roomNo"), chainId])

  const popupCloseHandler = () => {
    setDeletePublishUnPublishPopUp(false);
    setConfirmDeletePublishUnPublish("");
    setConfirmValidate(false);
    setPublishUnPublishPopUp(false);
  };

  const getPublishUnPublishListSidHandler = (row) => {
    setLoader(true);
    callAPI(`/welcome-message?Id=${row?.id}`, "GET", {}, { chainId: getChainIdLS() })
      .then(function (response) {
        // const checkinDateTime = new Date(response.checkinDateTime);
        // const checkoutDateTime = new Date(response.checkoutDateTime);
        const payload = {
          roomNo: response?.roomNo,
          cdsn: response?.cdsn,
          guestName: response?.guestName,
          // checkinDateTime: checkinDateTime.toISOString().split("T")[0],
          // checkOutDateTime: checkoutDateTime.toISOString().split("T")[0],
          welcomeMessage: response?.welcomeMessage
        };
        const loadedPreview = resolveGuestNameLive(
          response?.welcomeMessage || "",
          response?.guestName ?? ""
        );
        const loadedPreviewLen = loadedPreview.length;
        setApiWelcomeMessage(response?.welcomeMessage);
        setPreviewMessage(loadedPreview);
        setPreviewCharCount(loadedPreviewLen);
        if (loadedPreviewLen > MAX_CHARS) {
          wasOverLimitRef.current = false;
          setShowLimitAlert(true);
          wasOverLimitRef.current = true;
        }
        reset(payload);
        setEditPublishUnPublishUpdate(true);
        setLoader(false);
      })
      .catch(function (error) {
        console.warn(error.response?.data?.errorMessage);
        setLoader(false);
      });
  }

  const buildDefaultTemplate = (roomNo) => {
    const hotel = chainId?.navSelectedhotelName?.at(0)?.name;
    const roomDetail = roomDetails.find((r) => String(r.roomNo) === String(roomNo));
    const roomType = roomDetail?.roomType;
    const fromTemplates = resolveWelcomeMessageForRoom({
      roomNo,
      roomType,
      templates,
      customMessages: publishUnPublishList?.welcomeMessageResponse || [],
    });
    return (
      fromTemplates ||
      `Dear ${GUEST_NAME_PLACEHOLDER}, Welcome to ${hotel}`
    );
  };

  const clearRoomDependentState = () => {
    setCdsnList("");
    setApiWelcomeMessage("");
    setPreviewMessage("");
    setPreviewCharCount(0);
    setValue("cdsn", "");
    setValue("guestName", "Guest");
    setValue("welcomeMessage", "");
    clearErrors();
    wasOverLimitRef.current = false;
    setEditPublishUnPublishUpdate(false);
    setPublishUnPublishSid("");
    setEditPublishUnPublish(false);
  };

  const resetLayoutHandler = () => {
    reset({
      roomNo: '',
      cdsn: '',
      guestName: 'Guest',
      welcomeMessage: '',
    });
    clearRoomDependentState();
  }

  const cancelPublishUnPublishHandler = () => {
    resetLayoutHandler()
    // setEditPublishUnPublishUpdate(false);
    // setPublishUnPublishSid('');
    // setEditPublishUnPublish(false);
  }

  const publishUnPublishHandler = () => {
    setLoader(true);
    const apiPath = `/publish-unpublish-message?isPublish=${isPublishedUnPublishedRef?.current?.isPublishUnPublishModalData?.row?.isPublished.toLowerCase() !== 'published'}&Id=${isPublishedUnPublishedRef?.current?.isPublishUnPublishModalData?.row?.id}`;
    callAPI(apiPath, "POST", {}, { chainId: getChainIdLS() })
      .then((res) => {
        setFlag(!flag)
        let successMessage = res.message
        const newModalRefData = {
          isPublishUnPublishModalData: {
            message: successMessage
          }
        };
        isPublishedUnPublishedRef.current = newModalRefData;
        publishUnPublishListApi();
        setLoader(false);
      })
      .catch((err) => {
        console.warn(err.message);
        setPublishUnPublishPopUp(false);
        setLoader(false);
      });
  }

  // const validateCheckOutDate = (value) => {
  //   if (watch('checkinDateTime')) {
  //     const checkIn = new Date(watch('checkinDateTime'));
  //     const checkOut = new Date(value);
  //     if (checkIn >= checkOut) {
  //       return 'Check-out date must be after check-in date.';
  //     }
  //   }
  //   return true;
  // };

  //Pagination Handle changer
  const onPageChange = (event, value) => {
    setInputPageValue(value)
    setPaginationError("")
    setSelectedPage(value);
  };

  // useEffect(() => {
  //   if (editPublishUnPublish) {
  //     getPublishUnPublishListSidHandler();
  //     setEditPublishUnPublish(false);
  //   }
  //   ScrollToTop();
  //   setValue('guestName', 'Guest');
  // }, [editPublishUnPublish, chainId]);

  // When the user picks a room number (create mode), seed the editable
  // welcome-message field with the RAW template (including $$GUEST_NAME$$) and
  // initialise the preview with the substituted version. Edit mode is handled
  // separately by getPublishUnPublishListSidHandler.
  useEffect(() => {
    const roomNo = watch("roomNo");
    clearErrors("welcomeMessage");

    if (!roomNo) {
      clearRoomDependentState();
      return;
    }
    if (editPublishUnPublishUpdate) {
      return;
    }

    const template = buildDefaultTemplate(roomNo);
    const currentValue = watch("welcomeMessage") || "";

    if (!currentValue || currentValue === apiWelcomeMessage) {
      const resolved = resolveGuestNameLive(template, watch("guestName") || "");
      setValue("welcomeMessage", template);
      setApiWelcomeMessage(template);
      setPreviewMessage(resolved);
      setPreviewCharCount(resolved.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("roomNo"), templates, roomDetails, publishUnPublishList]);

  // Keep the preview in sync with both the guest name and the editable
  // welcome-message field so the right-hand preview updates live on every keystroke.
  useEffect(() => {
    if (!watch("roomNo")) {
      wasOverLimitRef.current = false;
      return;
    }
    const wmWatch = watch("welcomeMessage") || "";
    const resolved = resolveGuestNameLive(
      wmWatch,
      watch("guestName") || ""
    );
    const resolvedLength = resolved.length;
    setPreviewMessage(resolved);
    setPreviewCharCount(resolvedLength);
    if (resolvedLength <= MAX_CHARS) {
      wasOverLimitRef.current = false;
    } else if (!wasOverLimitRef.current) {
      setShowLimitAlert(true);
      wasOverLimitRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("guestName"), watch("welcomeMessage")]);

  const resetWelcomeMessageHandler = () => {
    // Only the template is reset to its initial state;
    const resolved = resolveGuestNameLive(
      apiWelcomeMessage || "",
      watch("guestName") || ""
    );
    setValue("welcomeMessage", apiWelcomeMessage || "");
    setPreviewMessage(resolved);
    setPreviewCharCount(resolved.length);
    clearErrors("welcomeMessage");
  };

  const welcomeMessageWatch = watch("welcomeMessage") || "";
  const guestStrLive = watch("guestName") || "";

  useLayoutEffect(() => {
    const textarea = welcomeMessageTextareaRef.current;
    const pendingSelection = pendingWelcomeMessageSelectionRef.current;

    if (!textarea || !pendingSelection) {
      return;
    }

    textarea.setSelectionRange(pendingSelection.start, pendingSelection.end);
    pendingWelcomeMessageSelectionRef.current = null;
  });

  const guestLenLive = guestStrLive.length;
  const previewLenLive = resolveGuestNameLive(
    welcomeMessageWatch,
    guestStrLive
  ).length;
  const maxGuestNameChars = getMaxGuestNameCharsForTemplate(welcomeMessageWatch);
  const guestCountStyle =
    maxGuestNameChars - guestLenLive < 10
      ? { color: "#dc3545" }
      : { color: "#6c757d" };
  const previewCountStyle = previewLenLive > MAX_CHARS
    ? { color: "#dc3545" }
    : previewLenLive >= MAX_CHARS - 10
      ? { color: "#dc3545" }
      : { color: "#6c757d" };
  const isOverLimit = previewCharCount > MAX_CHARS;

  return (
    <div className='row config-hotel'>
      <div className='col-12 left-column'>
         {loader && <div style={{zIndex: "99999"}} className="circular-progress"><CircularProgress /></div>}
        <form autoComplete="off" className="hotel-list-form">
          <div className="row">
            <div className="left-column-header">
              <p>{editPublishUnPublishUpdate ? 'Edit Welcome Message Configuration' : "Welcome Message Configuration"}</p>
            </div>
            <div className="row pe-0">
              <div className="col-4">
                <div className="form-group-hotel">
                  <div className="floating-select-label">
                    <Controller
                      name="roomNo"
                      control={control}
                      rules={{
                        validate: (value) => (value !== undefined && value !== null && value !== '') || 'Please select room Number',
                      }}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          className={`multiselectlist ${field?.value && field?.value?.length > 0 ? 'Mui-focused' : ''}`}
                          disablePortal
                          options={roomDetails}
                          getOptionLabel={(option) =>
                            typeof option === "string" ? option : option?.roomNo || ""
                          }
                          isOptionEqualToValue={(option, value) => {
                            const optNo =
                              typeof option === "string" ? option : option?.roomNo;
                            const valNo =
                              typeof value === "string" ? value : value?.roomNo;
                            return String(optNo) === String(valNo);
                          }}
                          onChange={(_, newValue) => {
                            const roomNo =
                              typeof newValue === "string"
                                ? newValue
                                : newValue?.roomNo;
                            field.onChange(roomNo ?? "");
                            setEditPublishUnPublishUpdate(false);
                            setPublishUnPublishSid("");
                            if (!roomNo) {
                              clearRoomDependentState();
                              return;
                            }
                            const template = buildDefaultTemplate(roomNo);
                            const resolved = resolveGuestNameLive(
                              template,
                              watch("guestName") || ""
                            );
                            setValue("welcomeMessage", template);
                            setApiWelcomeMessage(template);
                            setPreviewMessage(resolved);
                            setPreviewCharCount(resolved.length);
                          }}
                          value={
                            roomDetails.find(
                              (r) => String(r.roomNo) === String(field.value)
                            ) ||
                            field.value ||
                            null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder=""
                              variant="outlined"
                              label="Select Room Number"
                              InputLabelProps={{
                                shrink: false,
                              }}
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                  {errors.roomNo && (
                    <small className="required">{errors.roomNo?.message}</small>
                  )}
                </div>
                {/* <div className="row">
                  <div className="form-group-hotel col-6 mb-0">
                    <input
                      id="checkinDateTime"
                      type="date"
                      className="form-control"
                      min={today}
                      {...register("checkinDateTime", {
                        required: "Please Select Check In Date",
                      })}
                    />
                    {errors.checkinDateTime && (
                      <p className="required mb-0">{errors.checkinDateTime.message}</p>
                    )}
                  </div>
                  <div className="form-group-hotel col-6 mb-0">
                    <input
                      id="checkOutDateTime"
                      type="date"
                      className="form-control"
                      min={today}
                      {...register("checkOutDateTime", {
                        required: "Please Select Check Out Date",
                        validate: validateCheckOutDate
                      })}
                    />
                    {errors.checkOutDateTime && (
                      <p className="required mb-0">{errors.checkOutDateTime.message}</p>
                    )}
                  </div>
                </div> */}
              </div>

              {(watch("roomNo") !== "" && watch("roomNo") !== undefined && watch("roomNo") !== null) && <>
                <div className="form-group-hotel col-4">
                  <div className="floating-label">
                    <input
                      name="cdsn"
                      value={cdsnList}
                      disabled
                    />
                    <label htmlFor="cdsn">CDSN</label>
                  </div>
                </div>
                <div className="form-group-hotel col-4">
                  <div className={`floating-label`}>
                    <Controller
                      name="guestName"
                      control={control}
                      rules={{
                        required: "Enter Guest Name",
                        minLength: {
                          value: MIN_GUEST_NAME_CHARS,
                          message: `Guest name must be at least ${MIN_GUEST_NAME_CHARS} characters`,
                        },
                        pattern: {
                          value: /^[a-zA-Z .]+$/,
                          message: "Only letters, spaces and dots are allowed",
                        },
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="guestName"
                          maxLength={maxGuestNameChars}
                          placeholder=""
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const currentValue = field.value || "";
                            if (!/^[a-zA-Z .]*$/.test(newValue)) {
                              return;
                            }
                            const dynamicMax = getMaxGuestNameCharsForTemplate(
                              welcomeMessageWatch
                            );
                            if (
                              newValue.length > dynamicMax &&
                              newValue.length > currentValue.length
                            ) {
                              return;
                            }
                            const currentPreviewLen = resolveGuestNameLive(
                              welcomeMessageWatch,
                              currentValue
                            ).length;
                            const newPreviewLen = resolveGuestNameLive(
                              welcomeMessageWatch,
                              newValue
                            ).length;
                            if (
                              newPreviewLen > MAX_CHARS &&
                              newPreviewLen > currentPreviewLen
                            ) {
                              return;
                            }
                            field.onChange(newValue);
                          }}
                        />
                      )}
                    />
                    <label htmlFor="guestName">Enter Guest Name</label>
                  </div>
                  <small style={guestCountStyle} className="d-block mt-1">
                    {guestLenLive} / {maxGuestNameChars} characters
                  </small>
                  {errors.guestName && (
                    <small className="required">{errors.guestName.message}</small>
                  )}
                </div>
              </>}
              {(watch("roomNo") !== "" && watch("roomNo") !== undefined && watch("roomNo") !== null) && (
                <div className="row" style={{paddingRight: 0}}>
                  <div className="col-6 mb-3">
                    <label htmlFor="welcomeMessage" className="form-label fw-semibold mb-1">
                      Original Template
                    </label>
                    <Controller
                      name="welcomeMessage"
                      control={control}
                      rules={{ required: "Welcome Message is required" }}
                      render={({ field }) => (
                        <textarea
                          {...field}
                          ref={(element) => {
                            field.ref(element);
                            welcomeMessageTextareaRef.current = element;
                          }}
                          id="welcomeMessage"
                          style={{ height: '100%', minHeight: '80px' }}
                          placeholder={`Dear ${GUEST_NAME_PLACEHOLDER}, Welcome to ${chainId?.navSelectedhotelName?.at(0)?.name || ''}`}
                          onSelect={(e) => {
                            welcomeMessageSelectionRef.current = {
                              start: e.target.selectionStart ?? 0,
                              end: e.target.selectionEnd ?? 0,
                            };
                          }}
                          onKeyDown={(e) => {
                            welcomeMessageSelectionRef.current = {
                              start: e.currentTarget.selectionStart ?? 0,
                              end: e.currentTarget.selectionEnd ?? 0,
                            };
                          }}
                          onClick={(e) => {
                            welcomeMessageSelectionRef.current = {
                              start: e.target.selectionStart ?? 0,
                              end: e.target.selectionEnd ?? 0,
                            };
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const oldValue = field.value || "";
                            const insertStart = e.target.selectionStart ?? 0;
                            const insertEnd = e.target.selectionEnd ?? insertStart;
                            const pasteText = e.clipboardData.getData("text");
                            const fitted = fitWelcomeMessageInsert(
                              oldValue,
                              insertStart,
                              insertEnd,
                              pasteText,
                              guestStrLive
                            );

                            if (!fitted || fitted.value === oldValue) {
                              pendingWelcomeMessageSelectionRef.current = {
                                start: insertStart,
                                end: insertStart,
                              };
                              return;
                            }

                            pendingWelcomeMessageSelectionRef.current = {
                              start: fitted.cursorPos,
                              end: fitted.cursorPos,
                            };
                            field.onChange(fitted.value);
                          }}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const oldValue = field.value || "";
                            const charsDelta = newValue.length - oldValue.length;

                            if (!wouldExceedWelcomeMessageLimit(oldValue, newValue, guestStrLive)) {
                              field.onChange(newValue);
                              return;
                            }

                            if (charsDelta > 1) {
                              const { start: insertStart, end: insertEnd } =
                                welcomeMessageSelectionRef.current;
                              const suffixLen = oldValue.length - insertEnd;
                              const insertedText = newValue.slice(
                                insertStart,
                                newValue.length - suffixLen
                              );
                              const fitted = fitWelcomeMessageInsert(
                                oldValue,
                                insertStart,
                                insertEnd,
                                insertedText,
                                guestStrLive
                              );

                              if (fitted && fitted.value !== oldValue) {
                                pendingWelcomeMessageSelectionRef.current = {
                                  start: fitted.cursorPos,
                                  end: fitted.cursorPos,
                                };
                                field.onChange(fitted.value);
                                return;
                              }
                            }

                            rejectWelcomeMessageChange(
                              e.target,
                              oldValue,
                              newValue,
                              pendingWelcomeMessageSelectionRef
                            );
                          }}
                        />
                      )}
                    />

                    <small className="hint-text d-block mt-1">
                      Use <code>{GUEST_NAME_PLACEHOLDER}</code> as a placeholder wherever
                      you want the guest's name to appear.
                    </small>
                    
                    {errors.welcomeMessage && (
                      <small className="required">{errors.welcomeMessage.message}</small>
                    )}
                  </div>
                  <div className="col-6 mb-3" style={{ paddingRight: '0px' }}>
                    <label htmlFor="welcomeMessagePreview" className="form-label fw-semibold mb-1">
                      Preview
                    </label>
                    <Tooltip title="Read-only preview" arrow placement="top">
                      <textarea
                        id="welcomeMessagePreview"
                        value={previewMessage}
                        readOnly
                        style={{
                          height: '100%',
                          minHeight: '80px',
                          width: '100%',
                          cursor: 'not-allowed',
                          background: 'rgba(0, 0, 0, 0.03)',
                        }}
                        placeholder="Preview — $$GUEST_NAME$$ will be replaced with the guest's name"
                      />
                    </Tooltip>
                    <small style={previewCountStyle} className="d-block mt-1">
                      {previewLenLive} / {MAX_CHARS} characters
                    </small>
                  </div>
                </div>
              )}
              
            </div>
            <div className="col-12 text-end mt-5">
              {/* <div> */}
              <button type="button" className="clear-btn me-2" onClick={cancelPublishUnPublishHandler}>{editPublishUnPublishUpdate ? "Cancel" : "Clear All"}</button>
              {(watch("roomNo") !== "" && watch("roomNo") !== undefined && watch("roomNo") !== null) && (
                <button
                  type="button"
                  className="clear-btn me-2"
                  onClick={resetWelcomeMessageHandler}
                >
                  Reset Message
                </button>
              )}
              {editPublishUnPublishUpdate && <>
                <button type="button" className="clear-btn me-2" onClick={resetLayoutHandler}>Reset</button>
              </>}
              {!editPublishUnPublishUpdate && (
                <button
                  type="button"
                  disabled={isSubmitting || isOverLimit}
                  className={`disable-btn me-2 ${validPermissionClassName}`}
                  onClick={handleSubmit(onSave)}
                >
                  Save
                </button>
              )}
              <button
                type="button"
                disabled={isSubmitting || isOverLimit}
                className={`create-btn ${validPermissionClassName}`}
                onClick={handleSubmit(onSubmit)}
              >
                {editPublishUnPublishUpdate ? "Update" : "Check-In"}
              </button>
              {/* </div> */}
            </div>
          </div>
        </form>
        <div className="hotel-list">
          <p><b>Welcome Messages List</b></p>
          <TableWrapper
            data={publishUnPublishList?.welcomeMessageResponse}
            columns={columns}
            module="publish-unpublish"
            noPagination={true}
          />
          <Custom_Paingation
            selectedPage={selectedPage}
            totalPageCount={totalPageCount}
            paginationError={paginationError}
            inputPageValue={inputPageValue}
            setSelectedPage={setSelectedPage}
            setPaginationError={setPaginationError}
            setInputPageValue={setInputPageValue}
            onPageChange={onPageChange}
          />
        </div>
      </div>
      {/* <ConfigrationActions /> */}
      <DeleteCustomWelcomeMessage
        deletePublishUnPublishPopUp={deletePublishUnPublishPopUp}
        isPublishUnPublishDeletedRef={isPublishUnPublishDeletedRef}
        confirmDeletePublishUnPublish={confirmDeletePublishUnPublish}
        setConfirmDeletePublishUnPublish={setConfirmDeletePublishUnPublish}
        confirmValidate={confirmValidate}
        popupCloseHandler={popupCloseHandler}
        deletePublishUnPublishHandler={deletePublishUnPublishHandler}
      />
      <WelcomeMessageSuccessModal
        createPublishUnPublishSuccessMsg={createPublishUnPublishSuccessMsg}
        onOk={() => { setCreatePublishUnPublishSuccessMsg(''); setEditPublishUnPublishUpdate(false); }}
      />
      <WelcomeMessageCheckInOutModal
        publishUnPublishPopUp={publishUnPublishPopUp}
        isPublishedUnPublishedRef={isPublishedUnPublishedRef}
        popupCloseHandler={popupCloseHandler}
        publishUnPublishHandler={publishUnPublishHandler}
      />
      <WelcomeMessageCharLimitModal
        showLimitAlert={showLimitAlert}
        onOk={() => setShowLimitAlert(false)}
      />
    </div>
  )
}

export default CustomWelcomeMessageList;